import React, { useState, useEffect } from "react";

import { useSelector } from "react-redux";
import { NestedMenuItem } from "mui-nested-menu";

import { referenceEnabled, sitaEnabled } from "../../config";
import {
  Dialog,
  Box,
  TextField,
  Typography,
  Autocomplete,
  FormControl,
  Stack,
  Button,
  DialogContent,
  MenuItem,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { Container, Row, Col } from "react-bootstrap";

import DialogPaper from "../dialogPaper";
import { decodeHtmlEntities } from "../../utils/helpers";
import { parseSummary } from "./postHelpers";
import { patchNode } from "../../core/postNode";
import { usePostContext } from "../../contexts/postContext";
import DatePickerField from "../datePicker";

import { ChevronLeft } from "@mui/icons-material";

export function PostSettings(props) {
  const permissions = useSelector((state) => state).permissionsReducer
    .permissions;

  const {
    open,
    onClose,
    activeGridItem,
    entityType,
    onSaveSuccess,
    languages,
    categories,
    references,
    sitas,
  } = props;

  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [created, setCreated] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState(0);
  const [selectedSita, setSelectedSita] = useState(null);
  const [selectedReference, setSelectedReference] = useState(null);
  const [coreLibrary, setCoreLibrary] = useState(false);
  const [comments, setComments] = useState(false);
  const [reactions, setReactions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [categoryOpen, setCategoryOpen] = useState(false);

  useEffect(() => {
    if (activeGridItem && activeGridItem !== null) {
      spreadActiveData();
    }
    // eslint-disable-next-line
  }, [activeGridItem]);

  const allCategoryValues = React.useMemo(() => {
    return categories.map((c) => c.value);
  }, [categories]);

  const menuProps = {
    anchorOrigin: {
      vertical: "top",
      horizontal: "left",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "right",
    },
  };

  const renderMenuItems = (parentId = null) => {
    const items = categories.filter(
      (item) => item.parent_target_id === parentId,
    );

    return items.map((item) => {
      const children = categories.filter(
        (child) => child.parent_target_id === item.value,
      );

      if (children.length > 0) {
        if (!item.has_create_permission) {
          return null;
        }

        return (
          <NestedMenuItem
            key={`cat-${item.value}`}
            leftIcon={<ChevronLeft />}
            rightIcon={null}
            parentMenuOpen={categoryOpen}
            label={decodeHtmlEntities(item.label)}
            MenuProps={menuProps}
            className="settings-nested"
            onClick={() => {
              setCategoryOpen(false);
              setSelectedCategory(item.value);
            }}
          >
            {renderMenuItems(item.value)}
          </NestedMenuItem>
        );
      }

      if (!item.has_create_permission) {
        return null;
      }

      return (
        <MenuItem
          key={`cat-${item.value}`}
          value={item.value}
          sx={{
            fontSize: "16px",
            fontWeight: "400",
            "&.Mui-selected": {
              backgroundColor: "primary.light",
            },
          }}
          onClick={() => setSelectedCategory(item.value)}
        >
          {decodeHtmlEntities(item.label)}
        </MenuItem>
      );
    });
  };

  const spreadActiveData = () => {
    setTitle(activeGridItem.title);
    setSelectedCategory(activeGridItem.category_ids);
    setCreated(activeGridItem?.created);
    if (activeGridItem.language?.tid) {
      setSelectedLanguage(activeGridItem.language.tid);
    }
    if (activeGridItem.reference?.tid) {
      const match = (sitaEnabled ? sitas : references).find(
        (item) => parseInt(item.tid) === activeGridItem.reference.tid,
      );

      sitaEnabled ? setSelectedSita(match) : setSelectedReference(match);
    }
    const _summary = parseSummary(activeGridItem);
    if (_summary !== "") {
      setSummary(_summary);
    }
    if (activeGridItem?.field_allow_reactions) {
      setReactions(activeGridItem?.field_allow_reactions);
    }
    if (activeGridItem?.field_allow_comments) {
      setComments(activeGridItem?.field_allow_comments);
    }
    if (activeGridItem?.core_library) {
      setCoreLibrary(activeGridItem?.core_library);
    }
  };

  // Resets only the local state owned by this component.
  // The parent is responsible for clearing activeGridItem and any grid actions
  // inside its own onClose handler.
  const resetLocalState = () => {
    setTitle("");
    setSelectedCategory(0);
    setSelectedLanguage(null);
    setSelectedReference(null);
    setSelectedSita(null);
    setReactions(false);
    setComments(false);
    setCoreLibrary(false);
    setIsLoading(false);
  };

  const handleSave = () => {
    if (!props.persistOnSave) {
      resetLocalState();
    }
    onClose();
  };

  const handleClose = () => {
    if (props.persistOnSave) {
      spreadActiveData();
    } else {
      resetLocalState();
    }
    onClose();
  };

  const updateEntity = () => {
    const data = {
      title: title,
      field_category: [selectedCategory],
      language: parseInt(selectedLanguage),
      reference: selectedReference?.tid
        ? parseInt(selectedReference?.tid)
        : null,
      field_allow_comments: comments,
      field_allow_reactions: reactions,
      core_library: coreLibrary,
      created: created,
      summary: `<p>${summary}</p>`,
    };
    console.log("patchNode", data);
    patchNode(
      `api/v3/${entityType}/${activeGridItem.id}`,
      data,
      props.user.csrf_token,
    )
      .then(() => {
        handleSave();
        setIsLoading(false);
        onSaveSuccess?.();
      })
      .catch((_error) => {
        console.error("save error", _error);
        setIsLoading(false);
      });
  };

  // const processEditorData = () => {
  //   if (!activeGridItem?.body) {
  //     return {
  //       time: Date.now(),
  //       blocks: [{ type: "body", data: { text: "", summary } }],
  //     };
  //   }

  //   const tempBody = { ...activeGridItem.body };
  //   const targetBodyBlock = tempBody.blocks?.find((e) => e.type === "body");

  //   if (targetBodyBlock) {
  //     targetBodyBlock.data.summary = summary;
  //   }

  //   return tempBody;
  // };

  return (
    <Dialog
      className="ps-modal presentation-settings-modal"
      open={open}
      maxWidth={"lg"}
      fullWidth={true}
      onClose={handleClose}
      PaperComponent={DialogPaper}
    >
      <Box className="ps-modal-title dialog-draggable-handle">
        <Container fluid>
          <Row className="align-items-center">
            <Col xs={12} sm={6}>
              <Typography variant="h3" owner="admin" color="adminText.dark">
                Post settings (ID: {activeGridItem?.id})
              </Typography>
            </Col>
            <Col xs={12} sm={6}>
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
              >
                <Button
                  variant="outlined"
                  owner="admin"
                  dialogbtn="true"
                  dialogvariant="cancel"
                  className="lh"
                  onClick={handleClose}
                >
                  <Typography variant="button" owner="admin" className="lh">
                    Cancel
                  </Typography>
                </Button>
                <Button
                  variant="contained"
                  owner="admin"
                  dialogbtn="true"
                  dialogvariant="save"
                  className="lh"
                  disabled={isLoading}
                  onClick={() => {
                    setIsLoading(true);
                    updateEntity();
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography variant="button" owner="admin" className="lh">
                      Save
                    </Typography>
                  )}
                </Button>
              </Stack>
            </Col>
          </Row>
        </Container>
      </Box>
      <DialogContent>
        <Box className="ps-modal-content">
          <Container fluid>
            <Row>
              <Col xs={12} sm={6}>
                <Box className="spacer ls">
                  <Box className="form-group">
                    <Typography
                      className="vertical"
                      variant="form-group-label"
                      htmlFor="presentation-title"
                      owner="admin"
                    >
                      Title
                    </Typography>
                    <TextField
                      id="presentation-title"
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={title}
                      sx={{
                        "& .MuiInputBase-input": {
                          height: 28,
                        },
                      }}
                      onInput={(event) => {
                        setTitle(event.target.value);
                      }}
                    />
                  </Box>
                  <Box className="form-group">
                    <Typography
                      className="vertical"
                      variant="form-group-label"
                      htmlFor="summary"
                      owner="admin"
                    >
                      Summary
                    </Typography>
                    <TextField
                      id="summary"
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={summary}
                      sx={{
                        "& .MuiInputBase-root": {
                          minHeight: 45,
                          paddingTop: "3px",
                          paddingBottom: "3px",
                        },
                      }}
                      onInput={(event) => {
                        setSummary(event.target.value);
                      }}
                      multiline
                      maxRows={3}
                    />
                  </Box>
                  <Box className="form-group">
                    <DatePickerField
                      label="Date"
                      value={created}
                      unixTime={true}
                      onChange={setCreated}
                    />
                  </Box>
                  {permissions.access_promote_to_core_library && (
                    <Box className="form-group">
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="core-lib-checkbox"
                            checked={coreLibrary}
                            onChange={(event) => {
                              setCoreLibrary(event.target.checked);
                            }}
                          />
                        }
                        label={
                          <Typography variant="form-group-label" owner="admin">
                            Promote to Corporate library
                          </Typography>
                        }
                      />
                    </Box>
                  )}
                  {!props.disablereactions && (
                    <Box className="form-group">
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="reactions-checkbox"
                            checked={reactions}
                            onChange={(event) => {
                              setReactions(event.target.checked);
                            }}
                          />
                        }
                        label={
                          <Typography variant="form-group-label" owner="admin">
                            Allow reactions
                          </Typography>
                        }
                      />
                    </Box>
                  )}
                  {!props.disableComments && (
                    <Box className="form-group">
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="comments-checkbox"
                            checked={comments}
                            onChange={(event) => {
                              setComments(event.target.checked);
                            }}
                          />
                        }
                        label={
                          <Typography variant="form-group-label" owner="admin">
                            Allow comments
                          </Typography>
                        }
                      />
                    </Box>
                  )}
                </Box>
              </Col>
              <Col xs={12} sm={6}>
                <Box className="spacer rs">
                  <Box className="form-group">
                    <Typography
                      className="vertical"
                      variant="form-group-label"
                      htmlFor="presentationCategories"
                      owner="admin"
                    >
                      Category
                    </Typography>
                    <FormControl
                      variant={"outlined"}
                      size={"small"}
                      sx={{
                        width: 1,
                        "& label[data-shrink=true]": {
                          visibility: "hidden",
                        },
                        "& legend": {
                          width: 0,
                        },
                      }}
                    >
                      <InputLabel id={`category`}>Category</InputLabel>
                      <Select
                        labelId={`category-id`}
                        value={selectedCategory}
                        label={"Category"}
                        open={categoryOpen}
                        onClose={() => setCategoryOpen(false)}
                        onOpen={() => setCategoryOpen(true)}
                        renderValue={(selected) => {
                          const stringified = String(selected);

                          if (!stringified || stringified === "0")
                            return "All Categories";
                          const found = categories.find(
                            (c) => c.value === stringified,
                          );

                          return found
                            ? decodeHtmlEntities(found.label)
                            : "All Categories";
                        }}
                      >
                        {allCategoryValues.map((id) => (
                          <MenuItem
                            key={`register-${id}`}
                            value={id}
                            sx={{ display: "none" }}
                          />
                        ))}

                        <MenuItem
                          key="all-categories"
                          value={0}
                          sx={{
                            fontSize: "16px",
                            fontWeight: "400",
                            "&.Mui-selected": {
                              backgroundColor: "primary.light",
                            },
                          }}
                          disabled
                        >
                          All Categories
                        </MenuItem>

                        {renderMenuItems(null)}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box className="form-group">
                    <Typography
                      className="vertical"
                      variant="form-group-label"
                      htmlFor="languages"
                      owner="admin"
                    >
                      Language
                    </Typography>
                    <FormControl
                      variant={"outlined"}
                      size={"small"}
                      sx={{
                        width: 1,
                        "& label[data-shrink=true]": {
                          visibility: "hidden",
                        },
                        "& legend": {
                          width: 0,
                        },
                      }}
                    >
                      <InputLabel id={`Language`}>Language</InputLabel>
                      <Select
                        labelId={`language-id`}
                        value={selectedLanguage}
                        label={"Language"}
                        onChange={(event) => {
                          setSelectedLanguage(event.target.value);
                        }}
                      >
                        {languages?.map((language) => {
                          return (
                            <MenuItem
                              key={`language-${language.value}`}
                              value={language.value}
                              sx={{ fontSize: 16 }}
                              disabled={
                                language.value === 0 || language.value === "0"
                              }
                            >
                              {decodeHtmlEntities(language.label)}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Box>

                  {sitaEnabled && (
                    <Box className="form-group">
                      <Typography
                        className="vertical"
                        variant="form-group-label"
                        htmlFor="sita"
                        owner="admin"
                      >
                        SITA
                      </Typography>
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{
                          width: 1,
                          "& label[data-shrink=true]": {
                            visibility: "hidden",
                          },
                          "& legend": {
                            width: 0,
                          },
                        }}
                      >
                        <Autocomplete
                          options={sitas}
                          getOptionLabel={(option) => option.title}
                          value={selectedSita ? selectedSita : null}
                          disableClearable={true}
                          onChange={(event, value) => {
                            setSelectedSita(value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="SITA"
                              InputProps={{
                                ...params.InputProps,
                                type: "search",
                                sx: {
                                  paddingBottom: 0,
                                  "& .MuiOutlinedInput-input": {
                                    paddingBottom: "0 !important",
                                    paddingTop: "0 !important",
                                    borderColor: "#c2ccd6",
                                  },
                                  "&:hover .MuiOutlinedInput-input": {
                                    borderWidth: "2px",
                                    borderColor: "primary.main",
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </FormControl>
                    </Box>
                  )}

                  {referenceEnabled && (
                    <Box className="form-group">
                      <Typography
                        className="vertical"
                        variant="form-group-label"
                        htmlFor="reference"
                        owner="admin"
                      >
                        Reference
                      </Typography>
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{
                          width: 1,
                          "& label[data-shrink=true]": {
                            visibility: "hidden",
                          },
                          "& legend": {
                            width: 0,
                          },
                        }}
                      >
                        <Autocomplete
                          options={references}
                          getOptionLabel={(option) => option.title}
                          value={selectedReference ? selectedReference : null}
                          disableClearable={true}
                          onChange={(event, value) => {
                            setSelectedReference(value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Reference"
                              InputProps={{
                                ...params.InputProps,
                                type: "search",
                                sx: {
                                  paddingBottom: 0,
                                  "& .MuiOutlinedInput-input": {
                                    paddingBottom: "0 !important",
                                    paddingTop: "0 !important",
                                    borderColor: "#c2ccd6",
                                  },
                                  "&:hover .MuiOutlinedInput-input": {
                                    borderWidth: "2px",
                                    borderColor: "primary.main",
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </FormControl>
                    </Box>
                  )}
                </Box>
              </Col>
            </Row>
          </Container>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// Context dependent
export default function PostSettingsConnected(props) {
  const {
    showPostSettings,
    setShowPostSettings,
    setActiveGridItem,
    setActiveGridItemActions,
    activeGridItem,
    entityType,
  } = usePostContext();

  return (
    <PostSettings
      {...props}
      open={showPostSettings}
      onClose={() => {
        setShowPostSettings(false);
        setActiveGridItem(null);
        setActiveGridItemActions({});
      }}
      activeGridItem={activeGridItem}
      entityType={entityType}
    />
  );
}
